import { useEffect, useState, type ReactElement } from 'react';
import { Group, Loader, Text } from '@mantine/core';
import { Sparkles } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import type { VizAccent } from '@/design/VizAccents';
import type { TopicInsightsRequest } from '@/shared/assist/TopicInsightsContract';
import CalloutList from './CalloutList';
import { fetchTopicInsights } from './TopicInsightsClient';
import type { TopicCallout } from './TopicNarration';

interface InsightsRailCardProps {
    /** Client-derived callouts shown instantly and kept if the LLM call is absent, empty, or fails. */
    derived: TopicCallout[];
    accent: VizAccent;
    /** When set, the BFF LLM insights route is asked for richer bullets that swap in when they arrive. */
    request?: TopicInsightsRequest;
}

/**
 * The side rail's "Insights" card, catalog-free: renders {@link CalloutList} takeaways under a ✦ header.
 * With a `request` it behaves like {@link TopicInsightsPanel} — shows the derived set immediately, asks the
 * LLM insights route, and swaps in its bullets on success (keeping the derived set on failure/empty). Used
 * by pages that build their own context (Forecast, License Fit) rather than the topic catalog.
 */
export default function InsightsRailCard({ derived, accent, request }: InsightsRailCardProps): ReactElement | null {
    const [generated, setGenerated] = useState<TopicCallout[] | null>(null);
    const [generating, setGenerating] = useState(false);

    useEffect((): (() => void) => {
        let cancelled = false;
        setGenerated(null);
        if (request === undefined || request.rows.length === 0) return () => undefined;
        setGenerating(true);
        void fetchTopicInsights(request)
            .then((reply) => {
                if (!cancelled && reply.insights.length > 0) setGenerated(reply.insights.map((insight) => ({ tone: insight.tone, text: insight.text })));
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setGenerating(false);
            });
        return (): void => {
            cancelled = true;
        };
    }, [request]);

    const shown = generated ?? derived;
    if (shown.length === 0 && !generating) return null;

    return (
        <SurfaceCard $padding="md">
            <Group gap={6} mb="sm" wrap="nowrap" justify="space-between">
                <Group gap={6} wrap="nowrap">
                    <Sparkles size={14} style={{ color: 'var(--mantine-primary-color-filled)' }} />
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                        Insights
                    </Text>
                </Group>
                {generating && (
                    <Group gap={6} wrap="nowrap">
                        <Loader size={10} />
                        <Text size="xs" c="dimmed">
                            generating…
                        </Text>
                    </Group>
                )}
            </Group>
            <CalloutList callouts={shown} accent={accent} />
        </SurfaceCard>
    );
}
